import { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import styles from './Toast.module.css';

const ToastContext = createContext(null);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((toast) => {
        const id = Date.now();
        setToasts(prev => [...prev, { ...toast, id }]);

        // Auto remove after duration
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, toast.duration || 4000);

        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = {
        success: (title, message) => addToast({ type: 'success', title, message }),
        error: (title, message) => addToast({ type: 'error', title, message }),
        warning: (title, message) => addToast({ type: 'warning', title, message }),
        info: (title, message) => addToast({ type: 'info', title, message }),
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
}

function ToastContainer({ toasts, onRemove }) {
    const icons = {
        success: CheckCircle,
        error: XCircle,
        warning: AlertTriangle,
        info: Info,
    };

    return (
        <div className={styles.toastContainer}>
            {toasts.map(toast => {
                const Icon = icons[toast.type];
                return (
                    <div key={toast.id} className={`${styles.toast} ${styles[toast.type]}`}>
                        <Icon size={20} className={`${styles.icon} ${styles[toast.type]}`} />
                        <div className={styles.content}>
                            <div className={styles.title}>{toast.title}</div>
                            {toast.message && <div className={styles.message}>{toast.message}</div>}
                        </div>
                        <button className={styles.closeBtn} onClick={() => onRemove(toast.id)}>
                            <X size={16} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

export default ToastProvider;
